import React, { FC, memo, useCallback, useContext, useState } from 'react'
import { StyledGhostButton, CTAButton } from '../style'
import { ThemeContext } from 'styled-components'
import { useTranslation } from '../../../hooks/locale'
import { Typography } from 'react-components'
import get from 'lodash/get'
import useDynamicQueryParams from '../hooks/use-dynamic-query-params'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import getChildrenAges from '../utils/get-children-ages'
import AnalyticsService from '../../../services/analytics-service'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import UserDetailsModel from '../../../models/user/user-details-model'
import { PdpToCheckoutPosition } from '../constants'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { usePropertyDetailsContext } from '../../../hooks/property-details'
import { getCategory } from '../../../utils/category-utils'
import { useRouter } from 'next/router'

type ContactButtonProps = {
    bookingType: FormattedBookingTypeData | null
    searchbar: SearchbarFieldsReturn
    pricingDetails: FormattedLivePricingDataTransport | null
    inquiryContactDetails: InquiryContactDetailsTransport | null
    isPricingLoading: boolean
    isAvailabilityLoading: boolean
    pricingErrorCode: number | null
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const ContactButton: FC<ContactButtonProps> = props => {
    const theme = useContext(ThemeContext)
    const { t } = useTranslation()
    const { query } = useRouter()
    const { bookingType, searchbar, ...restProps } = props

    const dynamicQueryParams = JSON.parse(get(bookingType, 'subType.dynamicQueryParams', '{}'))
    const childrenAges = getChildrenAges(searchbar.kids)
    const qpValues = { ...query, ...searchbar, childrenAges }
    const url = useDynamicQueryParams(bookingType?.subType, qpValues, dynamicQueryParams)
    const type = get(bookingType, 'subType.type')
    const { isComboListing } = usePropertyDetailsContext()

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isEnquiryPopupOpen, setEnquiryPopupVisibility] = useState(false)
    const [EnquiryPopup, setEnquiryPopup] = useState<React.ComponentType<any> | null>(null)

    const {
        isProvidedVariantActive,
        isLoading: abLoading,
        error,
    } = useABVariant(AB_KEY.CHECKOUT_MERGER, AB_VARIANT_VERSION.ONE)

    const showEnquiryPopup = useCallback(async () => {
        if (!EnquiryPopup) {
            setIsLoading(true)
            const module = await import('../enquiry-button/enquiry-popup')
            setEnquiryPopup(() => module.default)
            setIsLoading(false)
        }
        setEnquiryPopupVisibility(true)
    }, [EnquiryPopup])

    const hideEnquiryPopup = useCallback(() => {
        setEnquiryPopupVisibility(false)
    }, [])

    const onContactButtonClick = useCallback(
        (event: React.MouseEvent) => {
            setTimeout(() => {
                AnalyticsService.trackEvent({
                    category: getCategory(isComboListing),
                    action: pdpPageActions.CONTACT_ENTER_DETAILS_BUTTON_CLICKED,
                })
            }, 0)

            if (type === 'redirect') {
                event.preventDefault()
                setIsLoading(true)
                window.open(url, '_blank')
                setIsLoading(false)
            } else {
                if (isProvidedVariantActive) {
                    event.preventDefault()
                    showEnquiryPopup()
                } else {
                    setIsLoading(true)
                    window.location.href = url
                    setTimeout(() => {
                        setIsLoading(false)
                    }, 5000)
                }
            }
        },
        [type, url, showEnquiryPopup, isProvidedVariantActive, isComboListing],
    )

    return (
        <>
            <CTAButton contentAlignment={theme.style.justifyContent.flexStart} margin={'1rem 0 0 0'}>
                <StyledGhostButton
                    isPrimary={false}
                    isContentWidth
                    onClick={onContactButtonClick}
                    disabled={isLoading || abLoading ? true : undefined}
                    loading={isLoading || abLoading ? true : undefined}
                >
                    <Typography variant="button" fontWeight="regular">
                        {t('contact.contactEnterDetails')}
                    </Typography>
                </StyledGhostButton>
            </CTAButton>
            {!!EnquiryPopup && (
                <EnquiryPopup
                    isOpen={isEnquiryPopupOpen}
                    onClose={hideEnquiryPopup}
                    searchbar={searchbar}
                    position={PdpToCheckoutPosition.CONTACT_BUTTON}
                    {...restProps}
                />
            )}
        </>
    )
}

export default memo(ContactButton)
